import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuApi, recipesApi } from '../services/api';
import ModifierGroupsManager, { useModifierGroups } from '../components/ModifierGroupsManager';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

interface MenuItem {
  id: string;
  name: string;
  shortName?: string;
  description?: string;
  priceCents: number;
  categoryId: string;
  categoryName?: string;
  isAvailable: boolean;
  prepTimeMinutes?: number;
  preparationTime?: number; // Legacy field name from API
  stations?: string[];
}

interface Category {
  id: string;
  name: string;
  itemCount?: number;
}

const KITCHEN_STATIONS = [
  { value: 'grill', label: 'Grill' },
  { value: 'fry', label: 'Fry' },
  { value: 'saute', label: 'Sauté' },
  { value: 'cold', label: 'Cold / Salad' },
  { value: 'pizza', label: 'Pizza' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'expo', label: 'Expo' },
  { value: 'bar', label: 'Bar' },
];

const defaultFormData = {
  name: '',
  shortName: '',
  description: '',
  priceCents: 0,
  priceDisplay: '', // Separate display value to avoid reformatting while typing
  categoryId: '',
  prepTimeMinutes: 10,
  stations: [] as string[],
};

export default function MenuPage() {
  const importFileRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const importMutation = useMutation({
    mutationFn: (f: File) => menuApi.importMenu(f),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      queryClient.invalidateQueries({ queryKey: ['menu-categories'] });
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      setImportResult(`Read it — ${d.items || 0} item${d.items === 1 ? '' : 's'} in ${d.categories || 0} new categor${d.categories === 1 ? 'y' : 'ies'} added${d.skippedExisting ? ` (${d.skippedExisting} already existed, left alone)` : ''}. Check the prices and you're live.`);
    },
    onError: (e: any) => setImportResult(e?.response?.data?.error?.message || "Couldn't read that file — a clearer photo or the original PDF usually works."),
  });
  const queryClient = useQueryClient();
  const { data: modifierGroups } = useModifierGroups();
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [formError, setFormError] = useState('');

  // Fetch categories
  const { data: categoriesData, error: categoriesError } = useQuery({
    queryKey: ['menu-categories'],
    queryFn: async () => {
      const response = await menuApi.getCategories();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch menu items
  const { data: itemsData, isLoading, error: itemsError } = useQuery({
    queryKey: ['menu-items'],
    queryFn: async () => {
      const response = await menuApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Show API error if any
  const apiError = categoriesError || itemsError;

  // Use real data only - no mock fallbacks
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const items = Array.isArray(itemsData) ? itemsData : [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => menuApi.createItem({
      name: data.name,
      shortName: data.shortName || undefined,
      description: data.description || undefined,
      priceCents: data.priceCents,
      categoryId: data.categoryId,
      prepTimeMinutes: data.prepTimeMinutes || undefined,
      stations: data.stations.length > 0 ? data.stations : undefined,
    }),
    onSuccess: async (res: any) => {
      const newItemId = res?.data?.data?.id;
      if (newItemId && selectedGroupIds.length > 0) {
        try {
          await menuApi.setItemModifierGroups(newItemId, selectedGroupIds);
        } catch {
          // group linking failed — item exists; user can re-save from Edit
        }
      }
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      queryClient.invalidateQueries({ queryKey: ['modifier-groups'] });
      setShowAddModal(false);
      setFormData(defaultFormData);
      setSelectedGroupIds([]);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create item');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      menuApi.updateItem(id, data),
    onSuccess: async (_res: any, vars: any) => {
      try {
        await menuApi.setItemModifierGroups(vars.id, selectedGroupIds);
      } catch {
        // group linking failed — the rest of the edit saved
      }
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      queryClient.invalidateQueries({ queryKey: ['modifier-groups'] });
      setShowEditModal(false);
      setEditingItem(null);
      setFormData(defaultFormData);
      setSelectedGroupIds([]);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to update item');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => menuApi.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Failed to delete item');
    },
  });

  // Toggle availability mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, isAvailable }: { id: string; isAvailable: boolean }) =>
      menuApi.updateAvailability(id, isAvailable),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
  });

  const filteredItems = items.filter((item: MenuItem) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.shortName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || item.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  // Live recipe-driven food cost per menu item (only items with linked recipes)
  const { data: marginsData } = useQuery({
    queryKey: ['menu-margins'],
    queryFn: async () => {
      const res = await recipesApi.getMargins();
      return (res.data?.data || res.data) as {
        items: Array<{ menuItemId: string; costPerPortionCents: number; foodCostPercent: number | null; isLowMargin: boolean }>;
      };
    },
    staleTime: 5 * 60 * 1000,
  });
  const marginByMenuItem = new Map((marginsData?.items || []).map((m) => [m.menuItemId, m]));

  const openEditModal = (item: MenuItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      shortName: item.shortName || '',
      description: item.description || '',
      priceCents: item.priceCents,
      priceDisplay: (item.priceCents / 100).toFixed(2),
      categoryId: item.categoryId,
      prepTimeMinutes: item.prepTimeMinutes || item.preparationTime || 10,
      stations: item.stations || [],
    });
    setSelectedGroupIds(
      (modifierGroups || [])
        .filter((g) => g.linkedItemIds.includes(item.id))
        .map((g) => g.id)
    );
    setShowEditModal(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this item?')) {
      deleteMutation.mutate(id);
    }
  };

  const renderModal = (isEdit: boolean) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{isEdit ? 'Edit Menu Item' : 'Add Menu Item'}</h2>
          <button
            onClick={() => {
              isEdit ? setShowEditModal(false) : setShowAddModal(false);
              setFormData(defaultFormData);
              setFormError('');
            }}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {formError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {formError}
          </div>
        )}
        <form onSubmit={(e) => {
          e.preventDefault();
          if (isEdit && editingItem) {
            updateMutation.mutate({ id: editingItem.id, data: formData });
          } else {
            createMutation.mutate(formData);
          }
        }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <input
                type="text"
                className="input w-full"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Short Name (for tickets)</label>
              <input
                type="text"
                className="input w-full"
                value={formData.shortName}
                onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                className="input w-full"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Price ($) *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  className="input w-full"
                  value={formData.priceDisplay}
                  onChange={(e) => {
                    // Allow typing freely without reformatting
                    const value = e.target.value;
                    // Only allow valid decimal input
                    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                      setFormData({
                        ...formData,
                        priceDisplay: value,
                        priceCents: Math.round(parseFloat(value || '0') * 100)
                      });
                    }
                  }}
                  onBlur={(e) => {
                    // Format nicely on blur
                    const value = parseFloat(e.target.value || '0');
                    setFormData({
                      ...formData,
                      priceDisplay: value.toFixed(2),
                      priceCents: Math.round(value * 100)
                    });
                  }}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prep Time (min)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="input w-full"
                  value={formData.prepTimeMinutes || ''}
                  onChange={(e) => setFormData({ ...formData, prepTimeMinutes: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-700">Category *</label>
                {/* Without this, a restaurant that skipped the onboarding
                    menu step can never add an item from this page */}
                <button
                  type="button"
                  onClick={async () => {
                    const name = window.prompt('New category name');
                    if (!name?.trim()) return;
                    try {
                      const res = await menuApi.createCategory({ name: name.trim() });
                      const newCat = res.data?.data;
                      queryClient.invalidateQueries({ queryKey: ['menu-categories'] });
                      if (newCat?.id) setFormData((f: any) => ({ ...f, categoryId: newCat.id }));
                    } catch {
                      alert("Couldn't create the category — please try again.");
                    }
                  }}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  + New category
                </button>
              </div>
              <select
                className="input w-full"
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                required
              >
                <option value="">Select category...</option>
                {categories.map((cat: Category) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            {(modifierGroups?.length || 0) > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Modifier groups <span className="text-slate-400 font-normal">(what the POS asks for this item)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {(modifierGroups || []).map((g) => {
                    const on = selectedGroupIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() =>
                          setSelectedGroupIds(on
                            ? selectedGroupIds.filter((id) => id !== g.id)
                            : [...selectedGroupIds, g.id])
                        }
                        className={
                          on
                            ? 'px-3 py-1.5 rounded-full text-sm font-medium bg-primary-100 text-primary-700 border border-primary-300'
                            : 'px-3 py-1.5 rounded-full text-sm font-medium bg-slate-100 text-slate-600 border border-transparent hover:bg-slate-200'
                        }
                      >
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kitchen Stations</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {KITCHEN_STATIONS.map((station) => (
                  <button
                    key={station.value}
                    type="button"
                    onClick={() => {
                      const stations = formData.stations.includes(station.value)
                        ? formData.stations.filter(s => s !== station.value)
                        : [...formData.stations, station.value];
                      setFormData({ ...formData, stations });
                    }}
                    className={clsx(
                      'px-3 py-1 rounded-full text-sm font-medium transition-colors',
                      formData.stations.includes(station.value)
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    )}
                  >
                    {station.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">Select which stations prepare this item</p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                isEdit ? setShowEditModal(false) : setShowAddModal(false);
                setFormData(defaultFormData);
                setFormError('');
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Item')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* API Error Banner */}
      {apiError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <p className="font-medium">Failed to load menu data</p>
          <p className="text-sm">Please check your connection and try again. Error: {(apiError as any)?.message || 'Unknown error'}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Menu</h1>
            <p className="text-slate-500">Manage menu items and categories</p>
          </div>
          <HelpButton featureId="menu" />
        </div>
        <div className="flex items-center gap-2">
          <input ref={importFileRef} type="file" accept=".pdf,.csv,image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importMutation.mutate(f); e.target.value = ''; }} />
          <button
            onClick={() => importFileRef.current?.click()}
            disabled={importMutation.isPending}
            className="btn btn-secondary flex items-center gap-2"
            title="A menu PDF, a photo of the printed menu, or a CSV — Mise reads it and builds the menu"
          >
            {importMutation.isPending ? 'Reading your menu…' : 'Import menu'}
          </button>
          <button
            onClick={() => { setSelectedGroupIds([]); setShowAddModal(true); }}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>
      </div>
      {importResult && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-4 py-3 flex items-center justify-between">
          <span>{importResult}</span>
          <button onClick={() => setImportResult(null)} className="opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Modifier groups — create/edit them here, attach on each item */}
      <ModifierGroupsManager />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search menu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10 w-full max-w-md"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={clsx(
            'px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors',
            selectedCategory === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-100'
          )}
        >
          All Items ({items.length})
        </button>
        {categories.map((category: Category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors',
              selectedCategory === category.id
                ? 'bg-primary-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100'
            )}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Menu Items Grid */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item: MenuItem) => (
            <div
              key={item.id}
              className={clsx('card p-4', !item.isAvailable && 'opacity-60')}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{item.name}</h3>
                    {!item.isAvailable && (
                      <span className="badge badge-danger">86'd</span>
                    )}
                  </div>
                  {item.shortName && (
                    <p className="text-sm text-slate-500">"{item.shortName}"</p>
                  )}
                  <p className="text-primary-600 font-bold mt-1">
                    {formatCurrency(item.priceCents)}
                    {(() => {
                      const m = marginByMenuItem.get(item.id);
                      if (!m || m.foodCostPercent === null) return null;
                      return (
                        <span
                          className={clsx(
                            'ml-2 align-middle text-xs font-medium px-1.5 py-0.5 rounded',
                            m.isLowMargin ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                          )}
                          title={`Costs ${formatCurrency(m.costPerPortionCents)} to make (from its recipe at current ingredient prices)`}
                        >
                          {m.foodCostPercent}% food cost
                        </span>
                      );
                    })()}
                  </p>
                </div>
                <button
                  onClick={() => toggleMutation.mutate({ id: item.id, isAvailable: !item.isAvailable })}
                  className={clsx(
                    'p-2 rounded-lg transition-colors',
                    item.isAvailable
                      ? 'text-success-600 hover:bg-success-50'
                      : 'text-slate-400 hover:bg-slate-100'
                  )}
                >
                  {item.isAvailable ? (
                    <ToggleRight className="w-6 h-6" />
                  ) : (
                    <ToggleLeft className="w-6 h-6" />
                  )}
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-slate-500">{item.categoryName}</span>
                {(item.prepTimeMinutes || item.preparationTime) && (
                  <span className="text-slate-500">~{item.prepTimeMinutes || item.preparationTime} min</span>
                )}
              </div>

              <div className="mt-3 pt-3 border-t flex items-center gap-2">
                <button
                  onClick={() => openEditModal(item)}
                  className="btn btn-ghost flex-1 text-sm"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="btn btn-ghost text-danger-600 text-sm"
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredItems.length === 0 && !isLoading && (
        <div className="text-center py-12 text-slate-500">
          <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-slate-600">Nothing here yet</p>
          <p className="mt-1 text-sm max-w-md mx-auto">
            Items land here from your website import during setup, from the assistant
            (paste any menu or price list in chat), or one at a time with Add Item above.
          </p>
        </div>
      )}

      {/* Modals */}
      {showAddModal && renderModal(false)}
      {showEditModal && renderModal(true)}
    </div>
  );
}
